import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, useTheme } from '@mui/material';
import axios from 'axios';
import qs from 'qs';
import { useWatch } from 'react-hook-form';
import { sdkClientPromise } from 'services/idonateSdk';
import { CCCvv, CCExpDate, CCSubheading } from 'components/CCPaymentInfo';
import Text from 'components/lib/Text';
import { useGivingFormData } from 'hooks';
import { useCreditCardToken } from 'hooks/useCreditCardToken';
import { useCreditCardValidation } from 'hooks/useCreditCardValidation';
import useWindowSize from 'hooks/useWindowSize';
import { acceptedCreditCards } from 'types';

const isMobile = () => {
  let check = false;
  try {
    // eslint-disable-next-line
    (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || (window as any).opera);
  } catch {
    // not empty
  }
  return check;
};

const CardConnectCreditCard = () => {
  const { width } = useWindowSize();
  const { palette } = useTheme();
  const { organizationId } = useGivingFormData();
  const [iframeSrc, setIframeSrc] = useState('');
  const [currentToken, setCurrentToken] = useState<string>();
  const isNarrow = width < 600;
  const { setPaymentProviderToken } = useCreditCardToken();
  const { creditCardCc, setCreditCardCc, setCreditCardCvv } =
    useCreditCardValidation();

  // Watch values to use for tokenizing
  const currentCVV = useWatch({ name: 'creditCard.cvv' });
  const currentExpiry = useWatch({ name: 'creditCard.expirationDate' });

  const init = useCallback(async () => {
    if (organizationId) {
      let orientation = 'custom';
      if (isNarrow) {
        orientation = 'vertical';
      }

      let css =
        'body {margin: 0;}input {border: 1px solid rgb(218, 220, 232);border-radius: 0.5rem;font-family: "Helvetica Neue", sans-serif;font-size: 1rem;letter-spacing: inherit;line-height: 1;height: 3rem;padding: 0 1rem;background-color: white;}input:hover {background-color: #EEF0FC;}input:hover, input:focus, input:active {border-color: rgba(25, 60, 172, 1);}label {display: none;}input#ccnumfield {width: calc(100.0% - 2.5rem);margin-right: 0.5rem;}input#ccexpiryfieldmonth {width: calc(12.5% - 3rem);margin-right: 0.75rem;}input#ccexpiryfieldyear {width: calc(12.5% - 2.625rem);margin-right: 0.75rem;}input#cccvvfield {width: calc(25.0% - 3.125rem);}';
      if (isNarrow) {
        css =
          'body {margin: 0;}input {border: 1px solid rgb(218, 220, 232);border-radius: 0.5rem;font-family: "Helvetica Neue", sans-serif;font-size: 1rem;letter-spacing: inherit;line-height: 1;height: 3rem;padding: 0 1rem;background-color: white;}input:hover {background-color: #EEF0FC;}input:hover, input:focus, input:active {border-color: rgba(25, 60, 172, 1);}label {font-family: "Helvetica Neue",sans-serif;font-weight: 700;font-size: 1rem;line-height: 1;color: #656677;margin-right: 0.5rem;margin-top: 0.5rem;line-height: 48px;}label#cccardlabel {display: none;}label#ccexpirylabel {width: 100%;display: inline-block;}label#cccvvlabel {width: 100%;}label[for="ccexpiryfieldyear"] {display: none;}input#ccnumfield {width: calc(100.0% - 2.25rem);}input#ccexpiryfieldmonth {width: 2rem;}input#ccexpiryfieldmonth+br {display:none;}input#ccexpiryfieldyear {width: 3rem; margin-left: 1rem;}input#cccvvfield {width: 3rem;}br:first-of-type {display: none;}';
      }
      const client = await sdkClientPromise(organizationId);
      const iframeSrcUrl = client.generateCardConnectTokenizerUrl();
      const [baseUrl] = iframeSrcUrl.split('?');

      // DOC: https://developer.cardpointe.com/hosted-iframe-tokenizer#iFrame-controls
      const newQueryParams = {
        enhancedresponse: 'true',
        usemonthnames: 'false',
        useexpiry: 'false',
        useexpiryfield: 'false',
        usecvv: 'false',
        placeholderyear: 'YYYY',
        invalidinputevent: 'true',
        cardnumbernumericonly: 'true',
        placeholder: 'Enter Card Number',
        placeholdermonth: 'MM',
        placeholdercvv: 'CVV',
        formatinput: 'true',
        expirymonthtitle: 'Month',
        orientation,
        unique: 'true',
        expirylabel: 'Expiration *',
        cvvlabel: 'CVV *',
        css,
        tokenizewheninactive: isMobile() ? 'true' : 'false',
        inactivityto: 1000
      };

      setIframeSrc(`${baseUrl}?${qs.stringify(newQueryParams)}`);
    }
  }, [organizationId, isNarrow]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    const handleMessageEvent = (event: MessageEvent) => {
      if (event.origin.includes('cardconnect.com')) {
        const cardData = JSON.parse(event.data);
        if (cardData.errorCode !== '0') {
          setCreditCardCc('error');
          // setCurrentToken(undefined);
        } else {
          setCreditCardCc('valid');
          // Don't set the token in state here. This will prevent us
          // from sending the token before we've attached the cvv/exp to it.
          setCurrentToken(cardData.message);
        }
      }
    };

    window.addEventListener('message', handleMessageEvent);

    return () => window.removeEventListener('message', handleMessageEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCreditCardCc, setCreditCardCvv]);

  useEffect(() => {
    const tokenizeCC = async (orgId: string) => {
      try {
        const client = await sdkClientPromise(orgId);
        const { data } = await axios.post(
          `${client.config.cardConnectBaseUrl}/cardsecure/api/v1/ccn/tokenize`,
          {
            account: currentToken,
            cvv: currentCVV,
            expiry: currentExpiry
          }
        );
        if (data.errorcode === 0) {
          setPaymentProviderToken(data.token);
        }
      } catch (err) {
        // tokenization failed at the execution level.
        // not sure if we need to reset this?
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };

    if (
      currentToken &&
      currentExpiry &&
      currentExpiry.length === 4 &&
      currentCVV &&
      currentCVV.length >= 3 &&
      organizationId
    ) {
      // Only update the token once we've got a token from the iframe, an expiration date that's
      // 4 digits, and a cvv that's at least 3 digits. From the frontend the CC iframe
      // doesn't tell us what brand of card we're dealing with so we can't determine if the
      // cvv should be 3 or 4 digits.
      tokenizeCC(organizationId);
    }
  }, [
    organizationId,
    currentToken,
    currentCVV,
    currentExpiry,
    setPaymentProviderToken
  ]);

  return iframeSrc ? (
    <>
      <Grid className="GF__GridInputWrapper" item xs={12} sm={6}>
        <Box display="flex" flexDirection="column">
          <CCSubheading currentCardType={acceptedCreditCards} />
          <iframe
            title="Card Connect"
            id="tokenframe"
            name="tokenframe"
            src={iframeSrc}
            scrolling="no"
            frameBorder="0"
            width="100%"
            height="52px"
          />
          {creditCardCc === 'error' && (
            <Text color={palette.red.main} variant="h6">
              Invalid credit card number
            </Text>
          )}
        </Box>
      </Grid>
      <CCExpDate />
      <CCCvv />
    </>
  ) : null;
};

export default CardConnectCreditCard;
